/* =============================================================
   La Tabl'O — Shopify theme interactions
   Scroll reveal, parallax hero, sticky showcase, nav state,
   manifesto word-by-word, interest form wizard, curtain intro
   (first-visit only, gated by sessionStorage).
   ============================================================= */

(() => {
  // Reveal styles are gated on html.js so content is visible by default and
  // only *hides* once the script that will reveal it is actually running.
  document.documentElement.classList.add('js');

  const prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ==========================================================
     Size recommendation v0.3: one structural model, both surfaces
     ----------------------------------------------------------
     Tray geometry measured off Prototype_wood_v2.4_mcp (Fusion)
     and the v2.3 STEP masters; the section is identical across
     the four SKUs, so the numbers hold for every size.

     The tray is a sunken well with a raised handle rail at each
     end. Relative to the plane it rests on:

         deck underside            26 mm below
         deck top (usable surface) 14 mm below
         rubber pad face            0      <- rests on the rim
         top of the handle rail    14 mm above

     The decision has one shape for every bath type:

       1. BATH[type] DESCRIBES the bath: how the rim width derives
          from the two tape readings, which rim widths describe a
          bath that actually exists, where the tray is designed to
          sit (a placement window and a target length), and where
          the tray's two ends land for a candidate length.
       2. VALIDATE the derived geometry. Readings that imply a rim
          no real bath has are sent back for a re-measure
          ('rim'), never answered confidently: the usual cause is
          B taken at the tub floor or the waterline instead of the
          top opening.
       3. PHYSICS filters candidates: each end needs enough pad on
          its rim, and the sunken deck must clear the opening.
       4. INTENT selects: the valid size closest to the designed
          target length wins; physical margin breaks ties. For
          freestanding that is 5 cm of overhang per side inside a
          3 to 8 cm band (approved 2026-07-20); for built-in it is
          flush with the far outer edge, drifting only as far as
          the rims dictate.

     Adding a bath type means adding one BATH entry. If a rule
     cannot be expressed in that table, it does not belong here.
     ========================================================== */
  const TabloFit = (() => {
    const DEFAULT_SIZES = [750, 800, 850, 900];

    // ---------- Pass / refuse thresholds: the safety rule, in one place ----------
    // A bath gets a size only if, at BOTH tray ends:
    //   1. rim per side is within [MIN_RIM, MAX_RIM]     -> else 'rim' (re-measure)
    //   2. pad touches at least `need` of the rim,       need = min(WANT_BEARING, rim - EDGE)
    //   3. the tray's belly clears the inner wall:       offset + rim <= SINK
    // Tune fit strictness here. SINK (the clearance bound) is computed from the CAD
    // descent profile just below, since it is geometry rather than a chosen threshold.
    const SAFETY = {
      MIN_RIM: 10,          // mm per side — the smallest rim we will size at all
      // Upper caps: a measured (outer-opening)/2 above this reads as implausible.
      // Freestanding raised 70 -> 85 (2026-07-21): flared-oval rims land at 7-8 cm
      // and seat fine; above ~8.5 cm the bearing/clearance physics is the real bound.
      MAX_RIM: { freestanding: 85, builtin: 95 },
      WANT_BEARING: 20,     // mm of silicone pad on the rim we ask for
      EDGE: 0.5,            // mm kept off the rim's inner lip (thin rim -> need rim - EDGE)
    };

    // Tray geometry, from CAD. The silicone pad is the only part that touches the
    // bath; SINK is where the profile drops below the rim plane.
    const PAD_NEAR = 6.5;    // pad starts this far in from the tray's end
    const PAD_FAR = 88.45;   // and ends here

    // The tray's descent past the rim plane, sampled off v2.3_900.step:
    // d = distance from the tray's end, h = depth below the rim plane.
    // The curve dives steeper than 1:1, which is why sloping bath walls
    // barely move the clearance bound.
    const DESCENT = [
      [96.7, 0], [97.5, 0.43], [101.5, 3.29], [105.5, 8.39],
      [109.5, 14.42], [113.5, 18.45], [117.5, 21.33], [121.5, 23.4],
      [125.5, 24.81], [129.5, 25.66], [133.5, 25.99], [136, 26],
    ];

    // Bath walls slope inward below the rim. Duravit spec sheets put the
    // full-depth side-wall average at 14 to 19 degrees from vertical, and
    // the top 26 mm (all the tray occupies) steeper than that: acrylic is
    // thermoformed with 3 to 8 degree draft. We allow twice the worst
    // measured average, 35 degrees. A wall only starts to bind past about
    // 41 degrees, so the computed bound equals the vertical-wall 96.7 mm;
    // the assumption is now declared and checked rather than implicit.
    // (Study 2026-07-20: Duravit No.1 / DuraSenja data sheets.)
    const WALL_SLOPE = 0.7;  // horizontal per vertical, tan(35 deg)
    const SINK = Math.min(...DESCENT.map((p) => p[0] - WALL_SLOPE * p[1]));

    // The baths. Lengths in mm; offset = how far the tray's end extends
    // past that end's outer rim edge (negative = stops short of it).
    const BATH = {
      freestanding: {
        // Tray centred on the bath, both ends alike.
        rim: (outer, inner) => (outer - inner) / 2,
        rimTrusted: [SAFETY.MIN_RIM, SAFETY.MAX_RIM.freestanding],
        // Placement rule, not a physical one: the tray should never look
        // short, so it must at least reach the bath's outer edge. There is
        // no upper bound here because physics supplies it, the pad runs off
        // the rim long before the overhang becomes silly.
        //
        // This used to be [outer+60, outer+160], a 3 to 8 cm per side band
        // carried over from v0.1, whose rationale ("the pad must clear the
        // thin outer wall") belonged to the superseded model where the tray
        // BRIDGED the bath on end feet. Under the real sunken-well geometry
        // the pads rest on the rim and `judgeEnd` already enforces the true
        // minimum. The band outlived its reason and was refusing fits that
        // seat perfectly: an 85 cm bath with 2.5 cm rims takes the 900 with
        // the pad covering the whole rim and 46.7 mm of clearance.
        window: (outer) => [outer, Infinity],
        target: (outer) => outer + 100,
        ends: (outer, L, rim) => {
          const o = (L - outer) / 2;
          return [{ offset: o, rim }, { offset: o, rim }];
        },
      },
      builtin: {
        // Tray pushed against the wall; only the far end moves.
        rim: (outer, inner) => outer - inner,
        rimTrusted: [SAFETY.MIN_RIM, SAFETY.MAX_RIM.builtin],
        window: () => [0, Infinity],   // bearing and clearance bound it
        target: (outer) => outer,
        ends: (outer, L, rim) => [
          { offset: 0, rim },
          { offset: L - outer, rim },
        ],
      },
    };

    const parseSizes = (csv) => {
      const list = String(csv || '')
        .split(',').map(s => parseInt(s.trim(), 10))
        .filter(Boolean).sort((a, b) => a - b);
      return list.length ? list : DEFAULT_SIZES.slice();
    };

    // Physics of one tray end on one rim.
    const judgeEnd = ({ offset, rim }) => {
      const bearing = Math.max(0,
        Math.min(PAD_FAR, offset + rim) - Math.max(PAD_NEAR, offset));
      const need = Math.min(SAFETY.WANT_BEARING, Math.max(0, rim - SAFETY.EDGE));
      const clear = SINK - (offset + rim);
      return { bearing, need, clear, ok: bearing >= need && clear >= 0,
               margin: Math.min(bearing - need, clear) };
    };

    // → { fit: true, sizeMm, rimMm, bearingMm, marginMm,
    //     protrusionMm (builtin) | overhangMm (freestanding) }
    //   { fit: false, reason: 'unmeasured' | 'inconsistent' | 'rim'
    //                        | 'narrow' | 'wide', rimMm? }
    const recommend = ({ bathType, outerCm, innerCm, sizes }) => {
      const model = BATH[bathType];
      const list = Array.isArray(sizes) ? sizes.slice().sort((a, b) => a - b)
                                        : parseSizes(sizes);
      const outerMm = Number(outerCm) * 10;
      const innerMm = Number(innerCm) * 10;
      if (!model || !outerMm || !innerMm) return { fit: false, reason: 'unmeasured' };
      if (innerMm >= outerMm) return { fit: false, reason: 'inconsistent' };

      const rim = model.rim(outerMm, innerMm);
      if (rim <= 1) return { fit: false, reason: 'inconsistent' };
      if (rim < model.rimTrusted[0] || rim > model.rimTrusted[1]) {
        return { fit: false, reason: 'rim', rimMm: rim };
      }

      const bounds = model.window(outerMm);
      const target = model.target(outerMm);

      let best = null;
      for (const L of list) {
        if (L < bounds[0] || L > bounds[1]) continue;
        const ends = model.ends(outerMm, L, rim).map(judgeEnd);
        if (!ends.some(e => !e.ok)) {
          const dist = Math.abs(L - target);
          const margin = Math.min(...ends.map(e => e.margin));
          if (!best || dist < best.dist - 0.01 ||
              (Math.abs(dist - best.dist) <= 0.01 && margin > best.margin)) {
            best = { L, ends, dist, margin };
          }
        }
      }

      if (!best) {
        if (list.every(L => L < bounds[0])) return { fit: false, reason: 'wide', rimMm: rim };
        if (list.every(L => L > bounds[1])) return { fit: false, reason: 'narrow', rimMm: rim };
        const probe = model.ends(outerMm, list[0], rim).map(judgeEnd);
        return { fit: false, reason: probe.some(e => e.clear < 0) ? 'narrow' : 'wide', rimMm: rim };
      }

      const worst = best.ends.reduce((a, b) => (a.bearing <= b.bearing ? a : b));
      const r = {
        fit: true,
        bathType,
        sizeMm: best.L,
        rimMm: rim,
        bearingMm: worst.bearing,
        marginMm: best.margin,
      };
      if (bathType === 'freestanding') r.overhangMm = (best.L - outerMm) / 2;
      else r.protrusionMm = best.L - outerMm;
      return r;
    };

    const cm = (mm) => (mm / 10).toFixed(1).replace(/\.0$/, '');

    // Plain language: what the tray does on the bath, nothing technical.
    // The seating line names the pads and never shows a second bare number
    // next to the overhang: two adjacent centimetre figures about different
    // things is how a reader collapses them into one (caught 2026-07-20).
    const fitNote = (r) => {
      if (!r.fit) return '';
      const seat = r.bearingMm >= r.rimMm - 0.5
        ? 'Underneath, its pads rest on the full width of your rim, both sides.'
        : `Underneath, its pads rest on at least ${cm(r.bearingMm)}&nbsp;cm of the rim, both sides.`;
      if (r.bathType === 'freestanding') {
        return `It reaches about ${cm(r.overhangMm)}&nbsp;cm past each side of your bath. ${seat}`;
      }
      const p = r.protrusionMm;
      if (Math.abs(p) <= 2) return `It runs wall to rim, near enough exactly. ${seat}`;
      if (p > 0) return `It stands about ${cm(p)}&nbsp;cm past the outer edge, into the room. ${seat}`;
      return `It stops about ${cm(-p)}&nbsp;cm short of the outer edge, sitting neatly inside. ${seat}`;
    };

    // One word for how much room to spare, for a reassurance line.
    const fitQuality = (r) => !r.fit ? null
      : r.marginMm >= 15 ? 'comfortable'
      : r.marginMm >= 5 ? 'good'
      : 'snug';

    // Would a small slip of the tape change the answer? Re-runs the same
    // pipeline at +-slip on each reading. Measured 2026-07-20: a third of
    // built-in fits turn on 5 mm, so a confident-looking answer can rest on
    // a reading the customer cannot make that precisely. Where that is true
    // we say so rather than presenting the result as settled.
    const isSteady = (input, slipMm) => {
      const slip = (slipMm == null ? 5 : slipMm) / 10;   // mm -> cm
      const base = recommend(input);
      const key = (r) => (r.fit ? 'S' + r.sizeMm : 'N' + r.reason);
      const k0 = key(base);
      const A = Number(input.outerCm), B = Number(input.innerCm);
      return [[slip, 0], [-slip, 0], [0, slip], [0, -slip]].every(([dA, dB]) =>
        key(recommend({ ...input, outerCm: A + dA, innerCm: B + dB })) === k0);
    };

    return { recommend, parseSizes, fitNote, fitQuality, isSteady, DEFAULT_SIZES,
             _internals: { PAD_NEAR, PAD_FAR, SINK, SAFETY, BATH, judgeEnd,
                           DESCENT, WALL_SLOPE } };
  })();

  /* ==========================================================
     TabloDiagram: the section drawing, shared by both surfaces.

     Geometry is inlined rather than fetched: it is 2 KB, it must never
     be a network round-trip away from the answer it illustrates, and an
     asset fetch is one more thing to fail on a slow bathroom connection.

     SECTION is the tray's HALF section per SKU, extracted from
     03_Engineering/CAD/Exports/Masterfiles_100726/v2.3_*.step.
     y = height above the rubber pad face, i.e. above the plane the tray
     rests on. PAD is the silicone anti-slip strip; only the part standing
     proud of the wood is drawn, because that is what touches the bath.

     The tray is ONE closed bent-laminated shell, so its section is one
     polygon: out along the underside, back along the top. Running both
     edges the same way closes it as a bowtie and draws two planks.
     ========================================================== */
  const TabloDiagram = (() => {
    const SECTION = {
      '750': { under: [[1.0,5.0],[3.0,3.0],[5.0,2.0],[91.0,1.68],[97.0,-0.43],[101.0,-3.29],[103.0,-5.42],[107.0,-11.73],[111.0,-16.61],[117.0,-21.33],[125.0,-24.81],[188.5,-26.0],[363.0,-26.0]], over: [[1.0,13.0],[3.0,13.9],[95.0,13.52],[103.0,10.99],[109.0,7.34],[113.0,3.67],[117.0,-1.72],[119.0,-5.09],[121.0,-7.62],[125.0,-10.93],[131.0,-13.45],[363.0,-14.0]] },
      '800': { under: [[1.0,5.0],[3.0,3.0],[5.0,2.0],[91.0,1.68],[97.0,-0.43],[101.0,-3.29],[103.0,-5.42],[107.0,-11.73],[111.0,-16.61],[117.0,-21.33],[125.0,-24.81],[188.5,-26.0],[388.0,-26.0]], over: [[1.0,13.0],[3.0,13.9],[95.0,13.52],[103.0,10.99],[109.0,7.34],[113.0,3.67],[117.0,-1.72],[119.0,-5.09],[121.0,-7.62],[125.0,-10.93],[131.0,-13.45],[388.0,-14.0]] },
      '850': { under: [[1.0,5.0],[3.0,3.0],[5.0,2.0],[91.0,1.68],[97.0,-0.43],[101.0,-3.29],[103.0,-5.42],[107.0,-11.73],[111.0,-16.61],[117.0,-21.33],[125.0,-24.81],[188.5,-26.0],[413.0,-26.0]], over: [[1.0,13.0],[3.0,13.9],[95.0,13.52],[103.0,10.99],[109.0,7.34],[113.0,3.67],[117.0,-1.72],[119.0,-5.09],[121.0,-7.62],[125.0,-10.93],[131.0,-13.45],[413.0,-14.0]] },
      '900': { under: [[1.0,5.0],[3.0,3.0],[5.0,2.0],[91.0,1.68],[97.0,-0.43],[101.0,-3.29],[103.0,-5.42],[107.0,-11.73],[111.0,-16.61],[117.0,-21.33],[125.0,-24.81],[188.5,-26.0],[438.0,-26.0]], over: [[1.0,13.0],[3.0,13.9],[95.0,13.52],[103.0,10.99],[109.0,7.34],[113.0,3.67],[117.0,-1.72],[119.0,-5.09],[121.0,-7.62],[125.0,-10.93],[131.0,-13.45],[438.0,-14.0]] }
    };
    const PAD = { d0: 6.5, d1: 88.45, proud: 2.0 };

    const DEPTH = 104, PADX = 96, PADTOP = 78, PADBOT = 40;
    const WALL_DEG = 14;                     // a representative bath wall
    const cm = (mm) => (mm / 10).toFixed(1).replace(/\.0$/, '');

    // Where each end of the tray sits, mirroring the module's own BATH model.
    const placement = (bathType, outerMm, L) => (bathType === 'freestanding'
      ? { left: (L - outerMm) / 2, right: (L - outerMm) / 2 }
      : { left: 0, right: L - outerMm });

    const outline = (L, originL) => {
      const sec = SECTION[String(L)];
      if (!sec) return null;
      const half = L / 2;
      const w = (d, mir) => (mir ? originL + L - d : originL + d);
      const edge = (prof) => {
        const out = prof.map(([d, z]) => [w(d, false), z]);
        for (let i = prof.length - 1; i >= 0; i--) {
          const [d, z] = prof[i];
          if (d < half - 0.5) out.push([w(d, true), z]);
        }
        return out;
      };
      return edge(sec.under).concat(edge(sec.over).reverse());
    };

    // view: 'whole' | 'left' | 'right'
    const render = (r, opts) => {
      const { bathType, outerCm, innerCm } = opts;
      const view = opts.view || 'whole';
      const A = Number(outerCm) * 10;
      const rim = bathType === 'freestanding'
        ? (A - Number(innerCm) * 10) / 2 : A - Number(innerCm) * 10;
      const L = r.sizeMm;
      const o = placement(bathType, A, L);
      const pts = outline(L, -o.left);
      if (!pts) return '';

      const w = Math.max(A, L) + 2 * PADX;
      const h = DEPTH + PADTOP + PADBOT;
      const shift = bathType === 'freestanding' ? Math.max(0, (L - A) / 2) : 0;
      const X = (mm) => mm + PADX + shift;
      const Y = (mm) => PADTOP - mm;
      const k = Math.tan(WALL_DEG * Math.PI / 180);
      const p = [];

      p.push(`<path class="tfd-water" d="M${X(rim)},${Y(-14)} L${X(rim + k * (DEPTH - 14))},${Y(-DEPTH)} `
        + `L${X(A - rim - k * (DEPTH - 14))},${Y(-DEPTH)} L${X(A - rim)},${Y(-14)} Z"/>`);

      const side = (mir) => {
        const s = mir ? -1 : 1, x0 = mir ? A : 0;
        return `M${X(x0)},${Y(-DEPTH)} L${X(x0)},${Y(0)} L${X(x0 + s * rim)},${Y(0)} `
          + `L${X(x0 + s * (rim + k * DEPTH))},${Y(-DEPTH)}`;
      };
      p.push(`<path class="tfd-bath" d="${side(false)}"/>`);
      p.push(`<path class="tfd-bath" d="${side(true)}"/>`);
      if (bathType !== 'freestanding') {
        p.push(`<rect class="tfd-wall" x="${X(0) - 46}" y="${Y(0) - 26}" width="46" height="${DEPTH + 26}" rx="2"/>`);
      }

      p.push(`<path class="tfd-tray" d="M${pts.map(q => `${X(q[0])},${Y(q[1])}`).join(' L')} Z"/>`);

      [[o.left, false], [o.right, true]].forEach(([off, mir]) => {
        const a1 = mir ? A + off - PAD.d1 : -off + PAD.d0;
        const b1 = mir ? A + off - PAD.d0 : -off + PAD.d1;
        const [x0, x1] = [Math.min(a1, b1), Math.max(a1, b1)];
        p.push(`<rect class="tfd-padbody" x="${X(x0)}" y="${Y(PAD.proud)}" width="${x1 - x0}" height="${PAD.proud}"/>`);
        const lo = Math.max(x0, mir ? A - rim : 0), hi = Math.min(x1, mir ? A : rim);
        if (hi > lo) p.push(`<rect class="tfd-pad" x="${X(lo)}" y="${Y(PAD.proud)}" width="${hi - lo}" height="${PAD.proud}"/>`);
      });

      // Two annotations at most, in words. Anchored away from the drawing so
      // nothing collides with the wall or runs off the frame.
      const notes = [{ tick: rim / 2, ty: 40, tx: rim + 14, anchor: 'start',
                       text: 'rests here, on your rim', short: 'on your rim' }];
      const far = o.right;
      const label = bathType === 'freestanding'
        ? (far > 4 ? `${cm(far)} cm proud` : null)
        : (far > 4 ? `${cm(far)} cm into the room`
          : far < -4 ? `${cm(-far)} cm short of the edge` : null);
      if (label) notes.push({ tick: A + far / 2, ty: 64, tx: A + far / 2 - 14,
                              anchor: 'end', text: label, key: true });

      notes.forEach((n) => {
        const ax = X(n.tick), top = Y(n.ty), tx = X(n.tx);
        const elbow = n.anchor === 'start' ? tx - 7 : tx + 7;
        p.push(`<line class="tfd-note" x1="${ax}" y1="${Y(17)}" x2="${ax}" y2="${top}"/>`);
        p.push(`<line class="tfd-note" x1="${ax}" y1="${top}" x2="${elbow}" y2="${top}"/>`);
        p.push(`<text class="tfd-label${n.key ? ' is-key' : ''}" x="${tx}" y="${top + 5}" `
          + `text-anchor="${n.anchor}" data-long="${n.text}"`
          + (n.short ? ` data-short="${n.short}"` : '') + `>${n.text}</text>`);
      });

      // Crop windows. The fit-critical detail is the ~120 mm at each end, so a
      // narrow screen shows an end at 4x the scale rather than asking anyone to
      // rotate or pinch. Rotation is not an option anyway: screen.orientation
      // .lock() does not exist in iOS Safari.
      const win = {
        whole: [0, w],
        left: [X(0) - 74, 250],
        right: [X(A + Math.max(o.right, 0)) - 176, 250],
      }[view] || [0, w];
      const alt = view === 'whole'
        ? `the ${L} mm tray resting on both rims, its middle sitting down inside the bath`
        : 'close view of one end, showing the pad resting on the rim';

      return `<svg class="tfd-svg" viewBox="${win[0]} 0 ${win[1]} ${h}" role="img" `
        + `aria-label="Diagram: ${alt}.">${p.join('')}</svg>`;
    };

    // Size labels in real pixels after layout, then drop any that no longer fit
    // the frame. Keying off rendered width instead is wrong: a cropped view is
    // small on screen yet perfectly legible.
    const fitLabels = (root) => {
      (root || document).querySelectorAll('.tfd-svg').forEach((svg) => {
        const rendered = svg.getBoundingClientRect().width;
        if (!rendered) return;
        const vb = svg.viewBox.baseVal;
        const scale = vb.width / rendered;
        svg.querySelectorAll('.tfd-label').forEach((t) => {
          t.style.display = '';
          t.style.fontSize = `${(13 * scale).toFixed(1)}px`;
        });
        svg.querySelectorAll('.tfd-label').forEach((t) => {
          const room = () => {
            const b = t.getBBox();
            return b.width < vb.width * 0.8
              && b.x > vb.x - 2 && b.x + b.width < vb.x + vb.width + 2;
          };
          t.textContent = t.dataset.long || t.textContent;
          // A cramped frame should shorten the wording, not lose the point.
          if (!room() && t.dataset.short) t.textContent = t.dataset.short;
          const fits = room();
          t.style.display = fits ? '' : 'none';
          const line = t.previousElementSibling;
          if (line && line.classList.contains('tfd-note')) line.style.display = fits ? '' : 'none';
          const tick = line && line.previousElementSibling;
          if (tick && tick.classList.contains('tfd-note')) tick.style.display = fits ? '' : 'none';
        });
      });
    };

    return { render, fitLabels, hasGeometry: (mm) => !!SECTION[String(mm)] };
  })();

  /* ==========================================================
     TabloFitPanel: the "See how it sits" disclosure and its expanded view.

     The sentence is the answer; the drawing is evidence. So it is disclosed
     rather than imposed, and opens by itself only when the answer is tippy
     (a 5 mm slip changes it) or counter-intuitive (the tray stops short of
     the bath's edge). Bespoke and re-measure outcomes get no drawing: a
     picture of a bath that does not work helps nobody.
     ========================================================== */
  const TabloFitPanel = (() => {
    // Decide the default crop from the container the drawing will actually
    // occupy, not from window width: the panel sits inside a card that may be
    // far narrower than the viewport. Falls back to the window only when the
    // panel is still collapsed and therefore has no measurable width.
    // null means "cannot tell yet" (collapsed panel, layout not settled).
    // Treating an unknown width as narrow was wrong: it silently cropped the
    // drawing on desktop whenever the panel had not been laid out.
    const cropFor = (host) => {
      const w = host.getBoundingClientRect().width;
      if (!w) return null;
      return w < 460 ? 'left' : 'whole';
    };

    // Markup for the collapsible panel under a result.
    const markup = (r, opts) => {
      if (!r.fit || !TabloDiagram.hasGeometry(r.sizeMm)) return '';
      const nearLabel = opts.bathType === 'freestanding' ? 'Left end' : 'Wall end';
      const farLabel = opts.bathType === 'freestanding' ? 'Right end' : 'Far end';
      const stopsShort = r.protrusionMm != null && r.protrusionMm < -4;
      const open = (!opts.steady || stopsShort) ? ' open' : '';
      const caveat = !opts.steady
        ? 'This one is close. Half a centimetre either way and we’d send you a different width, so it’s worth running the tape over it once more.'
        : 'A diagram from your two measurements, not a picture of your bathroom. If it looks nothing like your bath, the numbers are worth checking.';
      return `
        <details class="fitview"${open} data-fitview>
          <summary class="fitview__summary">
            <svg class="fitview__chev" width="13" height="13" viewBox="0 0 14 14" fill="none"
                 stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 5.5l4 4 4-4"/></svg>
            See how it sits
          </summary>
          <div class="fitview__pick" role="group" aria-label="Which part to show">
            <button type="button" data-fitcrop="whole">Whole bath</button>
            <button type="button" data-fitcrop="left">${nearLabel}</button>
            <button type="button" data-fitcrop="right">${farLabel}</button>
            <button type="button" class="fitview__expand" data-fitexpand aria-label="Open the diagram larger">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                   stroke-width="1.5" aria-hidden="true"><path d="M6 2H2v4M10 14h4v-4M14 6V2h-4M2 10v4h4"/></svg>
              Expand
            </button>
          </div>
          <div class="fitview__viz" data-fitviz></div>
          <p class="fitview__caveat">${caveat}</p>
        </details>`;
    };

    // Wire a rendered panel: crop switching, expand, first paint.
    const attach = (root, r, opts) => {
      const host = root.querySelector('[data-fitviz]');
      if (!host) return;
      let view = null, chosen = false;
      // `force` is for an explicit choice: the click handler has already set
      // `view`, so the change-check below would otherwise skip its re-render.
      const paint = (force) => {
        const next = chosen ? view : (cropFor(host) || view || 'whole');
        // Otherwise re-render only when the crop actually changes, so observing
        // our own size cannot feed back into an endless repaint.
        if (!force && next === view && host.firstChild) { TabloDiagram.fitLabels(root); return; }
        view = next;
        host.innerHTML = TabloDiagram.render(r, { ...opts, view });
        root.querySelectorAll('[data-fitcrop]').forEach(b =>
          b.classList.toggle('is-on', b.dataset.fitcrop === view));
        TabloDiagram.fitLabels(root);
      };
      root.querySelectorAll('[data-fitcrop]').forEach(b => {
        b.addEventListener('click', () => { view = b.dataset.fitcrop; chosen = true; paint(true); });
      });
      // A collapsed <details> has zero width, so nothing can be measured until
      // it opens. Repaint on the first open, and on resize while it is open.
      const det = root.querySelector('[data-fitview]');
      if (det) det.addEventListener('toggle', () => { if (det.open) paint(); });
      // A ResizeObserver covers the lot in one: collapsed panels that gain
      // width on open, layout that settles late, and rotation.
      if ('ResizeObserver' in window) new ResizeObserver(() => paint()).observe(host);
      else window.addEventListener('resize', paint);
      const exp = root.querySelector('[data-fitexpand]');
      if (exp) exp.addEventListener('click', () => Lightbox.open(r, opts, view, exp));
      host.addEventListener('click', () => Lightbox.open(r, opts, view, exp));
      paint();
    };

    /* The expanded view: a real modal dialog. Escape closes, Tab is trapped,
       focus returns to whatever opened it, body scroll is locked, and zoom has
       buttons and +/- keys rather than being gesture-only. */
    const Lightbox = (() => {
      const MIN = 1, MAX = 6;
      let el = null, lastFocus = null, ctx = null;
      let scale = 1, tx = 0, ty = 0;
      const pointers = new Map();
      let pinch = null, dragFrom = null;

      const surface = () => el.querySelector('.tfl__surface');
      const apply = () => {
        surface().style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
        el.querySelector('[data-zout]').disabled = scale <= MIN + 0.01;
        el.querySelector('[data-zin]').disabled = scale >= MAX - 0.01;
        el.querySelector('.tfl__hint').hidden = scale > 1.05;
      };
      const reset = () => { scale = 1; tx = 0; ty = 0; apply(); };
      const zoomTo = (next, cx, cy) => {
        const clamped = Math.min(MAX, Math.max(MIN, next));
        const rect = surface().getBoundingClientRect();
        const ox = (cx == null ? rect.left + rect.width / 2 : cx) - rect.left;
        const oy = (cy == null ? rect.top + rect.height / 2 : cy) - rect.top;
        const k = clamped / scale;
        tx -= (ox - rect.width / 2) * (k - 1);
        ty -= (oy - rect.height / 2) * (k - 1);
        scale = clamped;
        if (scale === MIN) { tx = 0; ty = 0; }
        apply();
      };
      const draw = (view) => {
        ctx.view = view;
        el.querySelector('.tfl__viz').innerHTML = TabloDiagram.render(ctx.r, { ...ctx.opts, view });
        el.querySelectorAll('.tfl__pick button').forEach(b =>
          b.classList.toggle('is-on', b.dataset.view === view));
        reset();
        TabloDiagram.fitLabels(el);
      };
      const onKey = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); close(); return; }
        if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomTo(scale * 1.4); return; }
        if (e.key === '-') { e.preventDefault(); zoomTo(scale / 1.4); return; }
        if (e.key !== 'Tab') return;
        const f = [...el.querySelectorAll('button:not([disabled])')];
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      function close() {
        if (!el) return;
        document.removeEventListener('keydown', onKey, true);
        document.body.style.overflow = '';
        el.remove(); el = null; pointers.clear(); pinch = null; dragFrom = null;
        if (lastFocus && lastFocus.isConnected) lastFocus.focus();
      }
      function open(r, opts, view, trigger) {
        if (el) return;
        ctx = { r, opts, view };
        lastFocus = trigger || document.activeElement;
        const free = opts.bathType === 'freestanding';
        el = document.createElement('div');
        el.className = 'tfl';
        el.setAttribute('role', 'dialog');
        el.setAttribute('aria-modal', 'true');
        el.setAttribute('aria-label', `How the ${r.sizeMm} mm tray sits on your bath`);
        el.innerHTML = `
          <div class="tfl__bar">
            <div class="tfl__pick">
              <button type="button" data-view="whole">Whole bath</button>
              <button type="button" data-view="left">${free ? 'Left end' : 'Wall end'}</button>
              <button type="button" data-view="right">${free ? 'Right end' : 'Far end'}</button>
            </div>
            <div class="tfl__tools">
              <button type="button" data-zout aria-label="Zoom out">&minus;</button>
              <button type="button" data-zin aria-label="Zoom in">+</button>
              <button type="button" data-close aria-label="Close">&times;</button>
            </div>
          </div>
          <div class="tfl__stage">
            <div class="tfl__surface"><div class="tfl__viz"></div></div>
            <p class="tfl__hint">Drag to move, pinch or scroll to zoom</p>
          </div>`;
        document.body.appendChild(el);
        document.body.style.overflow = 'hidden';
        el.querySelectorAll('.tfl__pick button').forEach(b => b.onclick = () => draw(b.dataset.view));
        el.querySelector('[data-close]').onclick = close;
        el.querySelector('[data-zin]').onclick = () => zoomTo(scale * 1.4);
        el.querySelector('[data-zout]').onclick = () => zoomTo(scale / 1.4);
        const st = el.querySelector('.tfl__stage');
        st.addEventListener('wheel', (e) => {
          e.preventDefault(); zoomTo(scale * (e.deltaY < 0 ? 1.12 : 0.89), e.clientX, e.clientY);
        }, { passive: false });
        st.addEventListener('dblclick', () => (scale > 1 ? reset() : zoomTo(2.5)));
        st.addEventListener('pointerdown', (e) => {
          st.setPointerCapture(e.pointerId);
          pointers.set(e.pointerId, e);
          if (pointers.size === 2) {
            const [a, b] = [...pointers.values()];
            pinch = { d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), s: scale };
          } else if (scale > 1) {
            dragFrom = { x: e.clientX - tx, y: e.clientY - ty };
          }
        });
        st.addEventListener('pointermove', (e) => {
          if (!pointers.has(e.pointerId)) return;
          pointers.set(e.pointerId, e);
          if (pointers.size === 2 && pinch) {
            const [a, b] = [...pointers.values()];
            const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            zoomTo(pinch.s * (d / pinch.d), (a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
          } else if (dragFrom) {
            tx = e.clientX - dragFrom.x; ty = e.clientY - dragFrom.y; apply();
          }
        });
        const release = (e) => {
          pointers.delete(e.pointerId);
          if (pointers.size < 2) pinch = null;
          if (!pointers.size) dragFrom = null;
        };
        st.addEventListener('pointerup', release);
        st.addEventListener('pointercancel', release);
        document.addEventListener('keydown', onKey, true);
        draw(view);
        el.querySelector('[data-close]').focus();
      }
      return { open, close };
    })();

    window.addEventListener('resize', () => TabloDiagram.fitLabels());
    return { markup, attach };
  })();

  /* ==========================================================
     Measuring diagrams, defined once and used by both surfaces.
     Both readings run ACROSS the bath's width (the short axis),
     which is the direction the tray spans, so in a view from
     above both arrows are vertical. Getting that wrong is what
     made the first version unreadable.
     ========================================================== */
  const TabloGuides = (() => {
    const INK = '#241F1B', BLUE = '#637B8C', RIM = '#E1D9CE', OPEN = '#FFFFFF';

    // A vertical dimension arrow with a lettered chip on it.
    const dim = (x, y1, y2, letter, colour) => `
      <g stroke="${colour}" stroke-width="1.8">
        <line x1="${x}" y1="${y1}" x2="${x}" y2="${y2}"/>
        <polygon points="${x},${y1} ${x - 5},${y1 + 11} ${x + 5},${y1 + 11}" fill="${colour}" stroke="none"/>
        <polygon points="${x},${y2} ${x - 5},${y2 - 11} ${x + 5},${y2 - 11}" fill="${colour}" stroke="none"/>
      </g>
      <circle cx="${x}" cy="${(y1 + y2) / 2}" r="13" fill="${colour}"/>
      <text x="${x}" y="${(y1 + y2) / 2 + 4.5}" text-anchor="middle" font-size="13"
            font-weight="700" fill="#F6F2EF">${letter}</text>`;

    // Dashed leader from a point on the bath out to its dimension arrow.
    const lead = (x1, x2, y, colour) =>
      `<line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${colour}" stroke-width="1"
             stroke-dasharray="4 3" stroke-opacity="0.5"/>`;

    const freestanding = {
      title: 'Two numbers, one tape',
      svg: `<svg viewBox="0 0 460 250" xmlns="http://www.w3.org/2000/svg" fill="none" font-family="Inter, sans-serif">
        <ellipse cx="180" cy="126" rx="140" ry="78" fill="${RIM}" stroke="${INK}" stroke-width="2"/>
        <ellipse cx="180" cy="126" rx="108" ry="48" fill="${OPEN}" stroke="#AB9781" stroke-width="1.5"/>
        <text x="180" y="131" text-anchor="middle" font-size="12" fill="#8A8178" font-style="italic">the opening</text>
        <text x="180" y="192" text-anchor="middle" font-size="10.5" fill="#8A8178" font-style="italic">the rim</text>
        ${lead(180, 400, 48, INK)}${lead(180, 400, 204, INK)}
        ${dim(400, 48, 204, 'A', INK)}
        ${lead(180, 356, 78, BLUE)}${lead(180, 356, 174, BLUE)}
        ${dim(356, 78, 174, 'B', BLUE)}
        <text x="180" y="234" text-anchor="middle" font-size="11" fill="${INK}" fill-opacity="0.5" font-style="italic">your bath, seen from above</text>
      </svg>`,
      a: '<b>A: right across the outside.</b> The full width of the bath, outer edge to outer edge.',
      b: '<b>B: right across the opening.</b> The gap you could drop a sponge into, inner edge of the rim to inner edge of the rim.',
      why: 'The tray hooks over both rims and its middle sits down inside the bath. A tells us where it can rest, B tells us it will drop in. Two numbers, and we can be certain rather than hopeful.',
    };

    const builtin = {
      title: 'Two numbers, one tape',
      svg: `<svg viewBox="0 0 460 250" xmlns="http://www.w3.org/2000/svg" fill="none" font-family="Inter, sans-serif">
        <rect x="46" y="22" width="274" height="22" fill="#E8E2DA"/>
        <g stroke="#AB9781" stroke-width="0.8" stroke-opacity="0.55">
          <line x1="96" y1="22" x2="96" y2="44"/><line x1="146" y1="22" x2="146" y2="44"/>
          <line x1="196" y1="22" x2="196" y2="44"/><line x1="246" y1="22" x2="246" y2="44"/>
          <line x1="296" y1="22" x2="296" y2="44"/>
        </g>
        <text x="183" y="38" text-anchor="middle" font-size="11" fill="#5A5753" font-weight="600">the wall</text>
        <rect x="46" y="44" width="274" height="166" fill="${RIM}" stroke="${INK}" stroke-width="2"/>
        <rect x="74" y="76" width="218" height="104" rx="10" fill="${OPEN}" stroke="#AB9781" stroke-width="1.5"/>
        <text x="183" y="132" text-anchor="middle" font-size="12" fill="#8A8178" font-style="italic">the opening</text>
        <text x="183" y="198" text-anchor="middle" font-size="10.5" fill="#8A8178" font-style="italic">the rim</text>
        ${lead(183, 400, 44, INK)}${lead(183, 400, 210, INK)}
        ${dim(400, 44, 210, 'A', INK)}
        ${lead(183, 356, 44, BLUE)}${lead(183, 356, 180, BLUE)}
        ${dim(356, 44, 180, 'B', BLUE)}
        <text x="183" y="238" text-anchor="middle" font-size="11" fill="${INK}" fill-opacity="0.5" font-style="italic">your bath, seen from above</text>
      </svg>`,
      a: '<b>A: wall to the far outside edge.</b> Start the tape against the wall and run it straight across to where the bath ends.',
      b: '<b>B: wall to where the bath opens.</b> Same tape, same line: stop where the flat rim ends and the bath drops away.',
      why: 'The tray hooks over both rims and its middle sits down inside the bath. A tells us where it can rest, B tells us it will drop in. Two numbers, and we can be certain rather than hopeful.',
    };

    return { freestanding, builtin };
  })();

  /* ---------- "Find my size" guide, in a modal ----------
     Shared by the product page and the password (waitlist) page. Markup comes
     from snippets/size-guide.liquid: a trigger row in the flow plus a modal
     dialog. onApply(sizeMm) runs when the customer clicks the apply button in
     the result, so each surface decides how a recommendation is applied
     (variant radio vs. waitlist width pill); the modal closes itself after. */
  const initSizeGuide = (sizeGuide, onApply) => {
    const availableSizes = TabloFit.parseSizes(sizeGuide.dataset.sizes);
    const openBtn = sizeGuide.parentElement.querySelector('[data-size-guide-open]');
    const closeBtn = sizeGuide.querySelector('[data-size-guide-close]');
    let lastFocus = null;

    const openModal = () => {
      lastFocus = document.activeElement;
      sizeGuide.hidden = false;
      document.body.classList.add('sg-modal-lock');
      closeBtn?.focus();
    };
    const closeModal = () => {
      sizeGuide.hidden = true;
      document.body.classList.remove('sg-modal-lock');
      if (lastFocus && lastFocus.isConnected) lastFocus.focus();
    };

    openBtn?.addEventListener('click', openModal);
    closeBtn?.addEventListener('click', closeModal);
    sizeGuide.addEventListener('click', (e) => { if (e.target === sizeGuide) closeModal(); });
    document.addEventListener('keydown', (e) => {
      // The fitview lightbox (.tfl) can sit on top of this modal and owns
      // Escape while it is open. Its capture-phase handler closes it (and
      // calls preventDefault) before this bubble-phase listener runs, so a
      // consumed Escape means a higher layer already acted on it.
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (!sizeGuide.hidden && !document.querySelector('.tfl')) closeModal();
    });

    /* Wizard: one step in view at a time so the card never scrolls.
       1 bath type · 2 how to measure + the two numbers · 3 result. */
    const state = { bathType: null };
    const steps = sizeGuide.querySelectorAll('[data-sg-step]');
    const backBtn = sizeGuide.querySelector('[data-sg-back]');
    const nextBtn = sizeGuide.querySelector('[data-sg-next]');
    const dots = sizeGuide.querySelectorAll('.size-guide-modal__dots span');
    const vizEl = sizeGuide.querySelector('[data-viz]');
    const noteEl = sizeGuide.querySelector('[data-note]');
    const outerInput = sizeGuide.querySelector('[data-outer-input]');
    const innerInput = sizeGuide.querySelector('[data-inner-input]');
    const resultEl = sizeGuide.querySelector('[data-result]');
    let step = 1;

    const goTo = (n) => {
      step = n;
      steps.forEach(s => s.classList.toggle('is-active', parseInt(s.dataset.sgStep, 10) === n));
      dots.forEach((d, i) => d.classList.toggle('is-on', i === n - 1));
      backBtn.hidden = n === 1;
      // Step 1 advances by choosing a type; step 3's action lives in the
      // result. Only step 2 uses the footer primary ("Find my width"). No
      // autofocus there: the keyboard would cover the diagram on mobile.
      nextBtn.hidden = n !== 2;
      // The fitview drawing measures its host, so repaint when the result shows.
      if (n === 3) TabloDiagram.fitLabels(resultEl);
    };

    const selectBathType = (type) => {
      state.bathType = type;
      sizeGuide.querySelectorAll('.size-guide__type').forEach(b => {
        b.classList.toggle('is-selected', b.dataset.bath === type);
      });
      const g = TabloGuides[type];
      if (!g) return;
      vizEl.innerHTML = g.svg;
      noteEl.innerHTML = g.a + ' ' + g.b + ' ' + g.why;
      goTo(2);
    };

    const compute = () => {
      const num = (el) => (el && el.value !== '' ? parseFloat(el.value) : null);
      const outerCm = num(outerInput);
      const innerCm = num(innerInput);
      if (!outerCm || !innerCm || !state.bathType) {
        // Stay on the numbers step; point at what is missing.
        (!outerCm ? outerInput : innerInput)?.focus();
        return;
      }
      const r = TabloFit.recommend({
        bathType: state.bathType,
        outerCm: outerCm,
        innerCm: innerCm,
        sizes: availableSizes,
      });

      if (!r.fit) {
        const kicker = r.reason === 'inconsistent' ? 'Check those two numbers'
          : r.reason === 'rim' ? 'Double-check measurement B'
          : 'Outside our standard range';
        const head = r.reason === 'inconsistent' ? 'B should be smaller than A'
          : r.reason === 'rim' ? 'That rim width looks unlikely'
          : 'A bespoke width';
        const body = r.reason === 'inconsistent'
          ? 'The opening sits inside the rim, so B is always the smaller of the two. Worth measuring again.'
          : r.reason === 'rim'
          ? 'Together these numbers would make the rim about ' + (r.rimMm / 10).toFixed(1).replace(/\.0$/, '') + ' cm wide, and baths aren\'t really built like that. B is the opening at the very top, rim edge to rim edge. If a re-measure still gives you these numbers, write to us and we\'ll work the width out with you.'
          : (r.reason === 'narrow'
              ? 'Your bath\'s opening is narrower than our standard widths drop into.'
              : 'Your bath is outside the range our standard widths cover.')
            + ' Write to us and we\'ll quote a bespoke edition from the atelier.';
        resultEl.innerHTML =
          '<div class="size-guide__result-inner size-guide__result--custom">'
          + '<p class="size-guide__result-kicker">' + kicker + '</p>'
          + '<h4>' + head + '</h4>'
          + '<p>' + body + '</p>'
          + '</div>';
        goTo(3);
        return;
      }

      const quality = TabloFit.fitQuality(r);
      const reassure = quality === 'comfortable' ? 'Room to spare.'
        : quality === 'good' ? 'A proper fit.' : 'A close fit, but a true one.';
      const steady = TabloFit.isSteady({
        bathType: state.bathType, outerCm: outerCm, innerCm: innerCm, sizes: availableSizes,
      });
      // Same dedupe as the waitlist wizard: an OPEN fitview panel carries the
      // re-measure caveat itself. The caution paragraph is rendered hidden
      // when a panel will open, and un-hidden below if the panel is collapsed
      // for space (small screens), so the close-call warning is never lost.
      const guidePanelHtml = TabloFitPanel.markup(r, { bathType: state.bathType, outerCm: outerCm, innerCm: innerCm, steady: steady });
      const caution = steady ? '' :
        '<p class="size-guide__result-caution"' + (guidePanelHtml ? ' hidden' : '') + '>This one is close enough that half a centimetre either way would change our answer. Worth running the tape over it a second time.</p>';
      const isFreestanding = state.bathType === 'freestanding';
      resultEl.innerHTML =
        '<div class="size-guide__result-inner">'
        + '<p class="size-guide__result-kicker">For your ' + (isFreestanding ? 'freestanding' : 'built-in') + ' bath</p>'
        + '<h4><span class="size-guide__result-size">' + r.sizeMm + '&nbsp;mm</span> &middot; The one that fits</h4>'
        + '<p class="size-guide__result-note">' + TabloFit.fitNote(r) + ' ' + reassure + '</p>'
        + caution
        + guidePanelHtml
        + '<button type="button" class="btn btn--primary size-guide__apply" data-apply-size="' + r.sizeMm + '">Select ' + r.sizeMm + '&nbsp;mm</button>'
        + '</div>';
      // On small screens the result step must fit without scrolling, so the
      // fitview starts collapsed there; opening it is one tap.
      if (window.matchMedia('(max-width: 560px)').matches) {
        const det = resultEl.querySelector('[data-fitview]');
        if (det && det.open) {
          det.open = false;
          resultEl.querySelector('.size-guide__result-caution')?.removeAttribute('hidden');
        }
      }
      goTo(3);
      TabloFitPanel.attach(resultEl, r, {
        bathType: state.bathType, outerCm: outerCm, innerCm: innerCm, steady: steady,
      });
    };

    sizeGuide.querySelectorAll('.size-guide__type').forEach(btn => {
      btn.addEventListener('click', () => selectBathType(btn.dataset.bath));
    });

    backBtn?.addEventListener('click', () => goTo(Math.max(1, step - 1)));
    nextBtn?.addEventListener('click', () => { if (step === 2) compute(); });
    [outerInput, innerInput].forEach(el => el?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); compute(); }
    }));

    resultEl?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-apply-size]');
      if (!btn) return;
      closeModal();
      onApply(btn.dataset.applySize);
    });
  };

  /* ---------- Curtain intro , first visit per session ---------- */
  const curtain = document.getElementById('curtain');
  if (curtain) {
    const key = curtain.dataset.curtainKey || 'tablo-curtain-seen';
    const seen = sessionStorage.getItem(key) === '1';
    if (prefersReduce || seen) {
      curtain.remove();
    } else {
      window.addEventListener('load', () => {
        // The O takes ~1.4 s to draw; lift once it has settled.
        setTimeout(() => {
          curtain.classList.add('is-done');
          sessionStorage.setItem(key, '1');
          setTimeout(() => curtain.remove(), 1400);
        }, 1650);
      });
    }
  }

  /* ---------- Bespoke heading O: capital O in display headings becomes the ring ---------- */
  (() => {
    if (!document.getElementById('sym-o-ring')) return;
    const vb = { ring: document.getElementById('sym-o-ring').getAttribute('viewBox'), mark: document.getElementById('sym-o-mark').getAttribute('viewBox') };
    const glyph = (mark, lower) => {
      const kind = mark ? 'mark' : 'ring';
      const wrap = document.createElement('span');
      wrap.className = 'bo' + (mark ? ' bo--mark' : '') + (lower ? ' bo--x' : '');
      wrap.innerHTML = '<svg viewBox="' + vb[kind] + '" aria-hidden="true" focusable="false"><use href="#sym-o-' + kind + '"/></svg><span class="bo__t">' + (lower ? 'o' : 'O') + '</span>';
      return wrap;
    };
    document.querySelectorAll('h1, h2, h3').forEach((h) => {
      if (h.closest('.manifesto__text, .nav, .curtain, [data-no-brand-o]')) return;
      // lowercase o only where a heading opts in (the hero line): data-brand-o="lower"
      const lower = h.getAttribute('data-brand-o') === 'lower';
      const hit = lower ? /[Oo]/ : /O/;
      const walker = document.createTreeWalker(h, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) if (hit.test(walker.currentNode.nodeValue)) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const text = node.nodeValue;
        const frag = document.createDocumentFragment();
        // wrap every word that holds an O so the line never breaks inside it
        const re = lower ? /\S*[Oo]\S*/g : /\S*O\S*/g; let last = 0, m;
        while ((m = re.exec(text))) {
          if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
          const word = document.createElement('span'); word.className = 'bo-w';
          const wt = m[0]; let wl = 0;
          for (let i = 0; i < wt.length; i++) {
            if (wt[i] !== 'O' && !(lower && wt[i] === 'o')) continue;
            if (i > wl) word.appendChild(document.createTextNode(wt.slice(wl, i)));
            word.appendChild(glyph(/Tabl[’']$/.test(wt.slice(Math.max(0, i - 5), i)), wt[i] === 'o'));
            wl = i + 1;
          }
          if (wl < wt.length) word.appendChild(document.createTextNode(wt.slice(wl)));
          frag.appendChild(word);
          last = m.index + wt.length;
        }
        if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
        node.parentNode.replaceChild(frag, node);
      });
    });
  })();

  /* ---------- Footer sign-off: the lockup writes itself once per session ---------- */
  const footerSign = document.querySelector('[data-footer-sign]');
  if (footerSign) {
    const signKey = 'tablo-footer-signed-v1';
    if (prefersReduce || sessionStorage.getItem(signKey) === '1') {
      footerSign.classList.add('is-in', 'is-done');
    } else {
      const signObs = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        footerSign.classList.add('is-in');
        sessionStorage.setItem(signKey, '1');
        signObs.disconnect();
      }, { threshold: 0.6 });
      signObs.observe(footerSign);
    }
  }

  /* ---------- Reveal observer ---------- */
  const revealEls = document.querySelectorAll('[data-reveal]');
  const revealObs = new IntersectionObserver((entries) => {
    entries.forEach((e, i) => {
      if (e.isIntersecting) {
        const delay = (i % 6) * 80;
        e.target.style.setProperty('--reveal-delay', `${delay}ms`);
        e.target.classList.add('is-in');
        revealObs.unobserve(e.target);
      }
    });
  }, { threshold: 0.05 });
  revealEls.forEach(el => revealObs.observe(el));

  /* ---------- Manifesto word-by-word ---------- */
  const manifesto = document.querySelector('.manifesto__text');
  if (manifesto) {
    const words = manifesto.querySelectorAll('[data-word]');
    words.forEach((w, i) => {
      w.style.transitionDelay = `${i * 60}ms`;
    });
    const mObs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          manifesto.classList.add('is-in');
          mObs.unobserve(e.target);
        }
      });
    }, { threshold: 0.15 });
    mObs.observe(manifesto);
  }

  /* ---------- Reveal safety net ----------
     IntersectionObserver covers normal scrolling; this sweep covers what it
     occasionally misses in practice: anchor jumps, find-in-page, End-key and
     programmatic scrolls, where an element can land mid-viewport and stay at
     opacity 0. Any unrevealed element intersecting the viewport gets revealed
     on the next scroll tick, and once after load. */
  const pendingReveal = new Set(revealEls);
  if (manifesto) pendingReveal.add(manifesto);
  const revealSweep = () => {
    if (!pendingReveal.size) return;
    const vh = window.innerHeight;
    pendingReveal.forEach((el) => {
      if (el.classList.contains('is-in')) { pendingReveal.delete(el); return; }
      const r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) {
        el.classList.add('is-in');
        pendingReveal.delete(el);
      }
    });
  };
  setTimeout(revealSweep, 900);
  // Environments where neither IO nor scroll events arrive reliably (throttled
  // frames, some embedded webviews) still converge: tick until nothing is left
  // to reveal, then stop entirely.
  const revealTick = setInterval(() => {
    revealSweep();
    if (!pendingReveal.size) clearInterval(revealTick);
  }, 1200);

  /* ---------- Nav state ---------- */
  // Assigned in the showcase block below; called from onScroll so the active
  // chapter frame tracks every scroll position, including jumps.
  let showcaseSync = null;

  const nav = document.getElementById('nav');
  const hero = document.querySelector('.hero');
  const hospitality = document.querySelector('.hospitality');
  const showcase = document.querySelector('.showcase');
  const darkSections = [hero, showcase, hospitality].filter(Boolean);

  const onScroll = () => {
    revealSweep();
    if (showcaseSync) showcaseSync();
    const y = window.scrollY || window.pageYOffset;

    const h = document.documentElement.scrollHeight - window.innerHeight;
    const pct = Math.min(1, Math.max(0, y / h));
    const rail = document.getElementById('railFill');
    if (rail) rail.style.width = (pct * 100) + '%';

    if (nav) {
      // Hysteresis: stick past 40, release only above the top again, so the logo morph never flaps at the threshold.
      nav.classList.toggle('is-stuck', y > (nav.classList.contains('is-stuck') ? 12 : 40));
      // Expose the live nav height so the progress rail can pin to its bottom edge.
      document.documentElement.style.setProperty('--nav-h', nav.offsetHeight + 'px');
    }

    const navBottom = 60;
    let light = false;
    for (const s of darkSections) {
      const r = s.getBoundingClientRect();
      if (r.top <= navBottom && r.bottom >= navBottom) { light = true; break; }
    }
    if (nav) nav.classList.toggle('is-light', light);

    if (hero && !prefersReduce) {
      const img = document.getElementById('heroImg');
      if (img) {
        const r = hero.getBoundingClientRect();
        if (r.bottom > 0 && r.top < window.innerHeight) {
          const p = Math.max(0, -r.top);
          img.style.transform = `scale(1.12) translate3d(0, ${p * 0.25}px, 0)`;
        }
      }
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- Active nav link — pathname match ---------- */
  const navLinks = document.querySelectorAll('.nav__links a');
  const here = window.location.pathname.replace(/\/+$/, '') || '/';
  navLinks.forEach(a => {
    try {
      const linkPath = new URL(a.href, window.location.origin).pathname.replace(/\/+$/, '') || '/';
      if (linkPath === here) a.classList.add('is-active');
    } catch (_) { /* malformed href — ignore */ }
  });

  /* ---------- Showcase sticky — active panel + product frame swap ---------- */
  const panels = document.querySelectorAll('.panel');
  const productFrames = document.querySelectorAll('.showcase__frame');

  const setActiveFrame = (idx) => {
    productFrames.forEach(img => {
      const match = parseInt(img.dataset.panel, 10) === idx;
      img.classList.toggle('is-active', match);
    });
  };

  if (showcase && panels.length) {
    const sObs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) showcase.classList.add('is-in'); });
    }, { threshold: 0.1 });
    sObs.observe(showcase);

    // Scroll-driven sync instead of an IntersectionObserver: the observer's
    // entry set only covers panels whose intersection just changed, which left
    // the frame lagging one chapter behind during fast scrolls and jumps.
    // Recomputing closest-to-centre across all four panels each tick keeps
    // text and frame locked together at any scroll position.
    showcaseSync = () => {
      const vc = window.innerHeight / 2;
      let best = null;
      let bestD = Infinity;
      panels.forEach(p => {
        const r = p.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        const d = Math.abs((r.top + r.bottom) / 2 - vc);
        if (d < bestD) { bestD = d; best = p; }
      });
      if (best && !best.classList.contains('is-active')) {
        panels.forEach(p => p.classList.remove('is-active'));
        best.classList.add('is-active');
        setActiveFrame(parseInt(best.dataset.panel, 10) || 1);
      }
    };
    showcaseSync();
  }

  /* ---------- Smooth anchor scrolling ---------- */
  document.querySelectorAll('a[href^="#"], a[href*="/#"]').forEach(link => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      const hashIndex = href.indexOf('#');
      if (hashIndex < 0) return;
      const id = href.slice(hashIndex + 1);
      if (!id) return;
      // Only hijack if we're on the same page
      const isLocal = href.startsWith('#') || href.startsWith(window.location.pathname);
      if (!isLocal) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: prefersReduce ? 'auto' : 'smooth', block: 'start' });
    });
  });

  /* ==========================================================
     Interest form — multi-step wizard, posts natively to Shopify
     ========================================================== */
  const interestForm = document.getElementById('interestForm');
  if (interestForm) {
    const state = { email: '', bathType: '', outerCm: null, innerCm: null, recommendedMm: null };
    const steps = interestForm.querySelectorAll('.interest__step');

    // If the server confirmed the post, jump to step 5 and hide step 1
    const confirmedStep = interestForm.querySelector('.interest__step[data-step="5"].is-active');
    if (confirmedStep) {
      steps.forEach(s => { if (s !== confirmedStep) s.classList.remove('is-active'); });
    }

    const goTo = (n) => {
      steps.forEach(s => s.classList.remove('is-active'));
      const target = interestForm.querySelector(`[data-step="${n}"]`);
      if (target) target.classList.add('is-active');
    };

    const errorEl = () => document.getElementById('measureError');

    const showMeasureError = (msg, focusEl) => {
      const el = errorEl();
      if (el) { el.textContent = msg; el.hidden = false; }
      if (focusEl) { focusEl.classList.add('is-invalid'); focusEl.focus(); }
    };

    const clearMeasureError = () => {
      const el = errorEl();
      if (el) { el.textContent = ''; el.hidden = true; }
      ['interest-outer', 'interest-inner'].forEach(id =>
        document.getElementById(id)?.classList.remove('is-invalid'));
    };

    // Returns true when both readings are present and sane. Anything that fails
    // keeps the customer on step 3 with a specific reason, rather than sending
    // them to a result they cannot act on.
    const readMeasurements = () => {
      const outerInput = document.getElementById('interest-outer');
      const innerInput = document.getElementById('interest-inner');
      const num = (el) => (el && el.value.trim() !== '' ? parseFloat(el.value) : null);
      const a = num(outerInput);
      const b = num(innerInput);
      clearMeasureError();

      if (a === null && b === null) {
        showMeasureError('We need both measurements to work out your width. If you cannot measure right now, use the skip link below.', outerInput);
        return false;
      }
      if (a === null || Number.isNaN(a)) {
        showMeasureError('Add measurement A, right across the outside of your bath.', outerInput);
        return false;
      }
      if (b === null || Number.isNaN(b)) {
        showMeasureError('Add measurement B, across the opening of your bath.', innerInput);
        return false;
      }
      if (a < 40 || a > 140) {
        showMeasureError('That does not look right for A. We expect something between 40 and 140 cm.', outerInput);
        return false;
      }
      if (b < 30 || b > 140) {
        showMeasureError('That does not look right for B. We expect something between 30 and 140 cm.', innerInput);
        return false;
      }
      if (b >= a) {
        showMeasureError('B should be smaller than A. The opening sits inside the rim, so it is always the shorter of the two.', innerInput);
        return false;
      }
      state.outerCm = a;
      state.innerCm = b;
      return true;
    };

    interestForm.querySelectorAll('[data-next]').forEach(btn => {
      btn.addEventListener('click', () => {
        const nextStep = parseInt(btn.dataset.next, 10);

        if (nextStep === 2) {
          const emailInput = document.getElementById('interest-email');
          if (!emailInput.value || !emailInput.checkValidity()) { emailInput.focus(); return; }
          state.email = emailInput.value.trim();
        }

        if (nextStep === 4) {
          if (btn.hasAttribute('data-skip')) {
            // Deliberate skip: no measurements, and we say so on the result.
            clearMeasureError();
            state.outerCm = null;
            state.innerCm = null;
          } else if (!readMeasurements()) {
            return;   // stay on step 3, error is showing
          }
          computeRecommendation();
        }

        goTo(nextStep);
      });
    });

    // Back links. Purely navigational, nothing is discarded, so a customer can
    // change their bath type or a measurement and come forward again.
    interestForm.querySelectorAll('[data-back]').forEach(btn => {
      btn.addEventListener('click', () => {
        clearMeasureError();
        goTo(parseInt(btn.dataset.back, 10));
      });
    });

    // Typing again clears a standing error so it never lingers while corrected.
    ['interest-outer', 'interest-inner'].forEach(id => {
      document.getElementById(id)?.addEventListener('input', clearMeasureError);
    });

    interestForm.querySelectorAll('.interest__option').forEach(opt => {
      opt.addEventListener('click', () => {
        interestForm.querySelectorAll('.interest__option').forEach(o => o.classList.remove('is-selected'));
        opt.classList.add('is-selected');
        state.bathType = opt.dataset.bath;
        populateGuide(state.bathType);
        setTimeout(() => goTo(3), 350);
      });
    });

    function populateGuide(type) {
      const el = document.getElementById('guideContent');
      const g = TabloGuides[type];
      if (!el || !g) return;
      el.innerHTML = `
        <h4>${g.title}</h4>
        <div class="guide-viz">${g.svg}</div>
        <p class="guide-primary">${g.a}</p>
        <p class="guide-primary">${g.b}</p>
        <p class="guide-why">${g.why}</p>`;
    }

    function computeRecommendation() {
      const el = document.getElementById('sizeResult');
      if (!el) return;

      if (!state.outerCm || !state.innerCm) {
        el.innerHTML = `<p>No measurements yet, and that's fine. We'll follow up by email and work the width out with you.</p>`;
        state.recommendedMm = null;
        return;
      }

      const r = TabloFit.recommend({
        bathType: state.bathType,
        outerCm: state.outerCm,
        innerCm: state.innerCm,
        sizes: interestForm.closest('[data-sizes]')?.dataset.sizes,
      });

      state.recommendedMm = r.fit ? r.sizeMm : null;

      if (r.fit) {
        const quality = TabloFit.fitQuality(r);
        const reassure = quality === 'comfortable' ? 'Room to spare.'
          : quality === 'good' ? 'A proper fit.' : 'A close fit, but a true one.';
        const steady = TabloFit.isSteady({
          bathType: state.bathType, outerCm: state.outerCm, innerCm: state.innerCm,
          sizes: interestForm.closest('[data-sizes]')?.dataset.sizes,
        });
        const panelOpts = {
          bathType: state.bathType, outerCm: state.outerCm,
          innerCm: state.innerCm, steady,
        };
        // When the fitview panel renders it auto-opens for close calls and
        // carries the same re-measure caveat, so the paragraph would say it
        // twice. Only render the paragraph when there is no panel.
        const panelHtml = TabloFitPanel.markup(r, panelOpts);
        const caution = (steady || panelHtml) ? '' :
          `<p class="size-caution">This one is close enough that half a centimetre either way would change our answer. Worth running the tape over it a second time.</p>`;
        el.innerHTML = `
          <p>For your ${state.bathType === 'freestanding' ? 'freestanding' : 'built-in'} bath</p>
          <span class="size-badge">${r.sizeMm} mm</span>
          <h3>The one that fits</h3>
          <p class="size-note">${TabloFit.fitNote(r)} ${reassure}</p>
          ${caution}
          ${panelHtml}`;
        TabloFitPanel.attach(el, r, panelOpts);
      } else if (r.reason === 'inconsistent') {
        el.innerHTML = `
          <h3>Those two numbers don't quite work</h3>
          <p>B should be smaller than A, since the opening sits inside the rim. Go back and check them, or leave your details and we'll help.</p>`;
      } else if (r.reason === 'rim') {
        el.innerHTML = `
          <h3>Double-check measurement B</h3>
          <p>Together these numbers would make your bath's rim about ${(r.rimMm / 10).toFixed(1).replace(/\.0$/, '')} cm wide, and baths aren't really built like that. B is the opening at the very top of the bath, from where the rim stops on one side to where it starts on the other. If a re-measure still gives you these numbers, register below and we'll work the width out with you.</p>`;
      } else {
        el.innerHTML = `
          <h3>Yours needs a bespoke width</h3>
          <p>${r.reason === 'narrow'
            ? "Your bath's opening is narrower than our standard widths drop into."
            : "Your bath is outside the range our standard widths cover."}
             Leave your details and we'll work out a bespoke width from the atelier.</p>`;
      }
    }

    // On real submit: populate hidden fields so Shopify stores full context,
    // then let the native form submission proceed. Enter on earlier steps
    // advances instead of submitting.
    interestForm.addEventListener('submit', (e) => {
      const activeStep = interestForm.querySelector('.interest__step.is-active');
      if (activeStep && activeStep.dataset.step !== '4') {
        e.preventDefault();
        const nextBtn = activeStep.querySelector('[data-next]');
        if (nextBtn) nextBtn.click();
        return;
      }
      const bathTypeHidden = document.getElementById('bathTypeHidden');
      const recommendedSizeHidden = document.getElementById('recommendedSizeHidden');
      if (bathTypeHidden) bathTypeHidden.value = state.bathType || '';
      if (recommendedSizeHidden) recommendedSizeHidden.value = state.recommendedMm || '';

      // Persist captured context into Shopify Customer note + tags.
      // Custom contact[*] keys are silently dropped by Shopify; note + tags survive.
      // Optional, skippable: the customer's own bath model. Pairing a stated
      // model with A/B + verdict is the one data point no catalog sells, so we
      // seed our rim lookup one signup at a time. Free text, never blocks submit.
      const bathModel = (document.getElementById('interest-model')?.value || '').trim().slice(0, 80);

      const noteEl = document.getElementById('interestNote');
      const tagsEl = document.getElementById('interestTags');
      const parts = [];
      if (state.bathType) parts.push('Bath type: ' + state.bathType);
      if (bathModel) parts.push('Bath model: ' + bathModel);
      if (state.outerCm) parts.push('A (outside): ' + state.outerCm + ' cm');
      if (state.innerCm) parts.push('B (opening): ' + state.innerCm + ' cm');
      if (state.recommendedMm) parts.push('Recommended size: ' + state.recommendedMm + ' mm');
      if (noteEl && parts.length) noteEl.value = parts.join('. ') + '.';

      if (tagsEl) {
        // Raw model stays in the note (it can contain commas, which split tags).
        // A marker tag makes the seed set filterable in the Customers admin.
        const tags = ['waitlist-v1', 'tablo-interest'];
        if (state.bathType) tags.push('bath-' + state.bathType);
        if (state.recommendedMm) tags.push('size-' + state.recommendedMm);
        if (bathModel) tags.push('has-bath-model');
        tagsEl.value = tags.join(', ');
      }
      // Let Shopify take it from here.
    });
  }

  /* ---------- FAQ accordion behavior ---------- */
  const faqItems = document.querySelectorAll('.faq__item');
  faqItems.forEach(item => {
    item.addEventListener('toggle', () => {
      if (item.open) {
        faqItems.forEach(other => {
          if (other !== item) other.open = false;
        });
      }
    });
  });

  /* ---------- Mobile menu toggle ---------- */
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobilePanel = document.querySelector('[data-mobile-panel]');
  if (menuToggle && mobilePanel) {
    const setOpen = (open) => {
      menuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      menuToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (open) {
        mobilePanel.hidden = false;
        // Force a reflow so the transform transition runs from -100% on open.
        void mobilePanel.offsetWidth;
        mobilePanel.classList.add('is-open');
        document.body.classList.add('is-menu-open');
      } else {
        mobilePanel.classList.remove('is-open');
        document.body.classList.remove('is-menu-open');
        // Wait for the slide-up transition before re-hiding so the close
        // animation can play.
        setTimeout(() => {
          if (menuToggle.getAttribute('aria-expanded') === 'false') mobilePanel.hidden = true;
        }, 450);
      }
    };
    menuToggle.addEventListener('click', () => {
      const isOpen = menuToggle.getAttribute('aria-expanded') === 'true';
      setOpen(!isOpen);
    });
    // Any link inside the panel closes it (including the Reserve CTA).
    mobilePanel.querySelectorAll('[data-mobile-link]').forEach((a) => {
      a.addEventListener('click', () => setOpen(false));
    });
    // Esc closes.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menuToggle.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        menuToggle.focus();
      }
    });
    // Close on resize past the desktop breakpoint to keep state coherent.
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.matchMedia('(min-width: 881px)').matches && menuToggle.getAttribute('aria-expanded') === 'true') {
          setOpen(false);
        }
      }, 120);
    });
  }

  /* ==========================================================
     Cart drawer — open/close + fetch cart + add-to-cart intercept
     ========================================================== */
  const drawer = document.getElementById('cart-drawer');
  const cartCountEl = document.querySelector('[data-cart-count]');

  const formatMoneyGeneric = (cents) => {
    const curr = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: curr }).format(cents / 100);
    } catch (e) {
      return '€' + (cents / 100).toFixed(2);
    }
  };

  const updateCartCount = (count) => {
    if (!cartCountEl) return;
    cartCountEl.textContent = count;
    if (count > 0) cartCountEl.removeAttribute('hidden');
    else cartCountEl.setAttribute('hidden', '');
  };

  const renderDrawerItems = (cart) => {
    if (!drawer) return;
    const body = drawer.querySelector('[data-cart-drawer-body]');
    const foot = drawer.querySelector('[data-cart-drawer-foot]');
    const empty = drawer.querySelector('[data-cart-drawer-empty]');
    const subtotal = drawer.querySelector('[data-cart-subtotal]');

    if (!cart.items || cart.items.length === 0) {
      body.innerHTML = '';
      foot.hidden = true;
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    foot.hidden = false;
    subtotal.textContent = formatMoneyGeneric(cart.items_subtotal_price);

    // Made-to-order banner for the drawer — surfaces atelier-batch context
    // when the cart contains items flagged via properties._preorder. The flag
    // name is kept for backwards compatibility; copy is neutral.
    const batchedItems = cart.items.filter(i => i.properties && i.properties._preorder);
    const hasBatched = batchedItems.length > 0;
    const hasMixed = hasBatched && cart.items.some(i => !(i.properties && i.properties._preorder));
    const shipWindow = hasBatched
      ? (batchedItems[0].properties._ship_window || 'the next atelier batch')
      : '';
    const bannerHtml = hasBatched
      ? '<aside class="cart-drawer__preorder">'
        + '<div class="cart-drawer__preorder-head">'
        + '<svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true">'
        + '<circle cx="9" cy="9" r="7"/><path d="M9 5.5v3.8l2.4 1.6" stroke-linecap="round"/></svg>'
        + '<b>Made-to-order &middot; ' + shipWindow + '</b>'
        + '</div>'
        + '<p>' + (hasMixed
            ? 'Your cart mixes in-stock and made-to-order items: <b>the whole order ships together</b> when the made-to-order pieces are ready. Place the in-stock pieces in a separate order if you need them sooner.'
            : 'Hand-finished in Belgium. We\'ll write before dispatch with your shipping window.')
        + '</p>'
        + '</aside>'
      : '';

    body.innerHTML = bannerHtml + cart.items.map(item => {
      // Shopify CDN images support ?width=NNN for on-the-fly resize, which works
      // regardless of URL structure (avoids fragile filename rewriting).
      const src = item.image
        ? (item.image + (item.image.includes('?') ? '&' : '?') + 'width=200')
        : '';
      const img = src
        ? '<img src="' + src + '" alt="" width="80" height="100" loading="lazy">'
        : '';
      const variant = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<p class="cart-drawer__variant">' + item.variant_title + '</p>' : '';
      return '<article class="cart-drawer__item" data-line-key="' + item.key + '">'
        + '<a class="cart-drawer__media" href="' + item.url + '">' + img + '</a>'
        + '<div class="cart-drawer__meta">'
        + '<h3><a href="' + item.url + '">' + item.product_title + '</a></h3>'
        + variant
        + '<div class="cart-drawer__qty">'
        + '<button type="button" class="cart-drawer__qty-btn" data-drawer-qty="-1" data-key="' + item.key + '" aria-label="Decrease">−</button>'
        + '<span class="cart-drawer__qty-value">' + item.quantity + '</span>'
        + '<button type="button" class="cart-drawer__qty-btn" data-drawer-qty="1" data-key="' + item.key + '" aria-label="Increase">+</button>'
        + '<button type="button" class="cart-drawer__remove" data-drawer-remove data-key="' + item.key + '">Remove</button>'
        + '</div>'
        + '</div>'
        + '<div class="cart-drawer__price">' + formatMoneyGeneric(item.final_line_price) + '</div>'
        + '</article>';
    }).join('');
  };

  const refreshCart = async () => {
    try {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await r.json();
      updateCartCount(cart.item_count);
      renderDrawerItems(cart);
      return cart;
    } catch (e) { return null; }
  };

  const openDrawer = async () => {
    if (!drawer) return;
    await refreshCart();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  };

  if (drawer) {
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); openDrawer(); });
    });
    drawer.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', () => closeDrawer());
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    /* Delegated qty / remove inside drawer */
    drawer.addEventListener('click', async (e) => {
      const qtyBtn = e.target.closest('[data-drawer-qty]');
      const removeBtn = e.target.closest('[data-drawer-remove]');
      if (qtyBtn) {
        const key = qtyBtn.dataset.key;
        const step = parseInt(qtyBtn.dataset.drawerQty, 10) || 0;
        const valueEl = qtyBtn.parentElement.querySelector('.cart-drawer__qty-value');
        const next = Math.max(0, (parseInt(valueEl?.textContent, 10) || 0) + step);
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: next })
        });
        refreshCart();
      } else if (removeBtn) {
        const key = removeBtn.dataset.key;
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        refreshCart();
      }
    });

    /* Intercept product add-to-cart forms */
    document.querySelectorAll('[data-product-form]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = form.querySelector('[data-submit]');
        const textEl = form.querySelector('[data-submit-text]');
        const originalText = textEl ? textEl.textContent : '';
        if (submitBtn) submitBtn.disabled = true;
        if (textEl) textEl.textContent = 'Adding…';
        try {
          const body = new FormData(form);
          const r = await fetch('/cart/add.js', { method: 'POST', body });
          if (!r.ok) throw new Error('Add failed');
          if (textEl) textEl.textContent = 'Added ✓';
          await openDrawer();
          setTimeout(() => { if (textEl) textEl.textContent = originalText; }, 1400);
        } catch (err) {
          if (textEl) textEl.textContent = 'Try again';
          setTimeout(() => { if (textEl) textEl.textContent = originalText; }, 1400);
        } finally {
          if (submitBtn) submitBtn.disabled = false;
        }
      });
    });
  }

  /* ==========================================================
     Cart page — shipping estimate stub
     ========================================================== */
  const estimate = document.querySelector('[data-estimate]');
  if (estimate) {
    const select = estimate.querySelector('[data-estimate-zone]');
    const note = estimate.querySelector('[data-estimate-note]');
    const notes = {
      BE: 'Free shipping across Belgium. Delivery window confirmed at checkout.',
      EU: 'Free shipping across EU mainland. Delivery window confirmed at checkout.'
    };
    select.addEventListener('change', () => { note.textContent = notes[select.value] || notes.EU; });
  }

  /* ==========================================================
     Product page — gallery, variant switching, qty
     ========================================================== */
  const productSection = document.querySelector('.product');
  if (productSection) {
    /* ---- Gallery (thumb -> frame) ---- */
    const thumbs = productSection.querySelectorAll('[data-thumb]');
    const frames = productSection.querySelectorAll('[data-frame]');
    thumbs.forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = btn.dataset.thumb;
        thumbs.forEach(t => {
          const on = t === btn;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        frames.forEach(f => {
          const on = f.dataset.frame === idx;
          f.classList.toggle('is-active', on);
          f.classList.remove('is-settled');
          if (on) f.addEventListener('animationend', () => f.classList.add('is-settled'), { once: true });
        });
      });
    });

    /* ---- Qty stepper ---- */
    const qtyInput = productSection.querySelector('.product__qty-input');
    productSection.querySelectorAll('[data-qty-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!qtyInput) return;
        const step = parseInt(btn.dataset.qtyStep, 10) || 0;
        const next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + step);
        qtyInput.value = next;
      });
    });

    /* ---- Variant switching ---- */
    const variantJson = productSection.querySelector('[data-variants]');
    const form = productSection.querySelector('[data-product-form]');
    if (variantJson && form) {
      let variants = [];
      try { variants = JSON.parse(variantJson.textContent); } catch (e) { variants = []; }
      const variantIdInput = form.querySelector('[data-variant-id]');
      const submitText = form.querySelector('[data-submit-text]');
      const submitBtn = form.querySelector('[data-submit]');
      const priceEl = productSection.querySelector('[data-price]');

      const formatMoney = (cents) => {
        const base = (Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
        // Lock to nl-BE so the JS-rendered price (after variant change) matches
        // the Liquid `| money` filter formatting on first paint — comma decimal,
        // €169,00 not €169.00.
        try {
          return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: base }).format(cents / 100);
        } catch (e) {
          return '€' + (cents / 100).toFixed(2).replace('.', ',');
        }
      };

      const readSelected = () => {
        const selected = [];
        form.querySelectorAll('.product__choice-input').forEach(input => {
          const idx = parseInt(input.dataset.optionIndex, 10);
          if (input.checked) selected[idx] = input.value;
        });
        return selected;
      };

      const findMatch = (selected) => {
        return variants.find(v => {
          const opts = [v.option1, v.option2, v.option3];
          return opts.every((o, i) => selected[i] == null || o === selected[i]);
        });
      };

      const waitlist = productSection.querySelector('[data-waitlist]');
      const waitlistVariantId = waitlist?.querySelector('[data-waitlist-variant-id]');
      const waitlistVariantTitle = waitlist?.querySelector('[data-waitlist-variant-title]');
      const preorderBlock = productSection.querySelector('[data-preorder-reassure]');

      // Annotate each option value with sold-out state based on currently-selected peers.
      // A value is "sold out" if every variant matching (other selected options + this value)
      // is unavailable.
      // In waitlist mode (pre-launch) we skip annotations entirely — nothing has launched yet.
      const isWaitlistMode = form.dataset.waitlistMode === 'true';
      const refreshOptionAvailability = (selected) => {
        if (isWaitlistMode) return;
        form.querySelectorAll('.product__choice-input').forEach(input => {
          const idx = parseInt(input.dataset.optionIndex, 10);
          const val = input.dataset.optionValue;
          const trial = selected.slice();
          trial[idx] = val;
          const matches = variants.filter(v => {
            const opts = [v.option1, v.option2, v.option3];
            return opts.every((o, i) => trial[i] == null || o === trial[i]);
          });
          const anyAvailable = matches.some(v => v.available);
          const label = input.nextElementSibling;
          const note = label?.querySelector('[data-choice-note]');
          if (matches.length === 0 || !anyAvailable) {
            label?.classList.add('is-soldout');
            if (note) note.textContent = 'Sold out';
          } else {
            label?.classList.remove('is-soldout');
            if (note) note.textContent = '';
          }
        });
      };

      const applyVariant = (variant) => {
        if (!variant) {
          if (submitBtn) { submitBtn.disabled = true; submitBtn.hidden = true; }
          if (preorderBlock) preorderBlock.hidden = true;
          if (waitlist) waitlist.hidden = false;
          return;
        }
        if (variantIdInput) variantIdInput.value = variant.id;
        if (priceEl && !priceEl.hasAttribute('data-price-locked')) {
          // Pricing hidden until launch for products without a public price.
          // data-price-locked marks a Liquid-rendered flat price (The Original:
          // one price across every variant) that must not be blanked here.
          priceEl.innerHTML = '—';
        }
        if (submitBtn) {
          submitBtn.disabled = !variant.available;
          submitBtn.hidden = !variant.available ? true : false;
        }
        if (submitText) {
          submitText.textContent = 'Add to cart';
        }
        if (preorderBlock) {
          preorderBlock.hidden = !variant.available;
        }
        if (waitlist) {
          waitlist.hidden = variant.available;
          if (waitlistVariantId) waitlistVariantId.value = variant.id;
          if (waitlistVariantTitle) waitlistVariantTitle.value = variant.title || '';
          // Reset any previous submit state when switching variants
          waitlist.querySelector('[data-waitlist-success]')?.setAttribute('hidden', '');
          waitlist.querySelector('[data-waitlist-error]')?.setAttribute('hidden', '');
          waitlist.querySelector('.product__waitlist-form')?.removeAttribute('hidden');
          waitlist.querySelector('.product__waitlist-lede')?.removeAttribute('hidden');
          waitlist.querySelector('.product__waitlist-fine')?.removeAttribute('hidden');
          waitlist.querySelector('.product__waitlist-btn')?.removeAttribute('disabled');
          // Keep the hidden variant-id / variant-title inputs in sync with the
          // selected variant, and clear the typed email so the next sold-out
          // variant starts from a clean state.
          const emailField = waitlist.querySelector('[data-waitlist-email]');
          if (emailField) emailField.value = '';
          const vIdField = waitlist.querySelector('[data-waitlist-variant-id]');
          const vTitleField = waitlist.querySelector('[data-waitlist-variant-title]');
          if (vIdField) vIdField.value = variant.id;
          if (vTitleField) vTitleField.value = variant.title || '';
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };

      form.querySelectorAll('.product__choice-input').forEach(input => {
        input.addEventListener('change', () => {
          const selected = readSelected();
          refreshOptionAvailability(selected);
          applyVariant(findMatch(selected));
        });
      });

      // Initial paint — annotate sold-out choices on first load.
      refreshOptionAvailability(readSelected());
    }

    /* ---- Size guide ("Find my size" modal) ---- */
    const sizeGuide = productSection.querySelector('[data-size-guide]');
    if (sizeGuide && form) {
      // Click "Select XXX mm" in result → programmatically pick the Size radio
      initSizeGuide(sizeGuide, (mm) => {
        // Variant values carry a "750 mm" space since the July 2026 Width rename;
        // fall back to the old space-less format so either matches.
        const radio = form.querySelector('.product__choice-input[data-option-value="' + mm + ' mm"]')
          || form.querySelector('.product__choice-input[data-option-value="' + mm + 'mm"]');
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          radio.closest('.product__option')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }

    /* ---- Waitlist block (sold-out pre-order) ----
       The waitlist is a <div> inside the product form — using a <form> here
       would nest forms and the browser would hijack the submit button to add
       the line item to the cart. The "submit" is a button[type=button], and
       this handler POSTs to Supabase directly. */
    const waitlist = productSection.querySelector('[data-waitlist]');
    const waitlistSubmit = waitlist?.querySelector('[data-waitlist-submit]');
    if (waitlist && waitlistSubmit) {
      waitlistSubmit.addEventListener('click', async () => {
        const supabaseUrl = waitlist.dataset.supabaseUrl;
        const supabaseKey = waitlist.dataset.supabaseKey;
        const emailEl = waitlist.querySelector('[data-waitlist-email]');
        const variantIdEl = waitlist.querySelector('[data-waitlist-variant-id]');
        const variantTitleEl = waitlist.querySelector('[data-waitlist-variant-title]');
        const successEl = waitlist.querySelector('[data-waitlist-success]');
        const errorEl = waitlist.querySelector('[data-waitlist-error]');
        const inputsRow = waitlist.querySelector('.product__waitlist-form');

        if (!emailEl || !emailEl.value.trim() || !emailEl.checkValidity()) {
          emailEl?.focus();
          return;
        }

        successEl?.setAttribute('hidden', '');
        errorEl?.setAttribute('hidden', '');
        waitlistSubmit.disabled = true;

        const showSuccess = () => {
          if (inputsRow) inputsRow.hidden = true;
          successEl?.removeAttribute('hidden');
          waitlist.querySelector('.product__waitlist-lede')?.setAttribute('hidden', '');
          waitlist.querySelector('.product__waitlist-fine')?.setAttribute('hidden', '');
        };

        const payload = {
          email: emailEl.value.trim(),
          source: 'waitlist',
          waitlist_variant_id: parseInt(variantIdEl?.value, 10) || null,
          waitlist_variant_title: variantTitleEl?.value || null,
          waitlist_product_handle: waitlist.dataset.productHandle || null,
        };

        if (!supabaseUrl || !supabaseKey) {
          console.warn('[waitlist] Supabase is not configured in theme settings — no waitlist row will be written. Payload would have been:', payload);
          if (inputsRow) inputsRow.hidden = true;
          waitlist.querySelector('.product__waitlist-lede')?.setAttribute('hidden', '');
          waitlist.querySelector('.product__waitlist-fine')?.setAttribute('hidden', '');
          const fallback = waitlist.querySelector('[data-waitlist-fallback]');
          if (fallback) fallback.hidden = false;
          if (errorEl) {
            errorEl.innerHTML = 'Our waitlist backend isn\'t configured yet. Please use the link below instead.';
            errorEl.hidden = false;
          }
          waitlistSubmit.disabled = false;
          return;
        }

        try {
          const res = await fetch(`${supabaseUrl}/rest/v1/interest_signups`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': supabaseKey,
              'Authorization': `Bearer ${supabaseKey}`,
              'Prefer': 'return=minimal',
            },
            body: JSON.stringify(payload),
          });
          // 409 = unique_violation (same email already on the waitlist for this
          // variant). We treat it as success because the intent is satisfied.
          if (!res.ok && res.status !== 409) throw new Error(`${res.status} ${await res.text()}`);
          showSuccess();
        } catch (err) {
          console.error('[waitlist] submit failed', err);
          errorEl?.removeAttribute('hidden');
          waitlistSubmit.disabled = false;
        }
      });

      // Enter-in-email-input triggers the submit button (since no form semantics)
      waitlist.querySelector('[data-waitlist-email]')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); waitlistSubmit.click(); }
      });
    }
  }

  /* ==========================================================
     Cart page — qty steppers + remove
     ========================================================== */
  /* ---------- Size guide on the password (waitlist) page ----------
     Same snippet + engine as the product page; applying a recommendation
     checks the matching width pill, which re-triggers the page's own
     tag/note rebuild via the synthetic change event. */
  const pwdWaitlistForm = document.getElementById('pwdWaitlist');
  const pwdSizeGuide = pwdWaitlistForm?.querySelector('[data-size-guide]');
  if (pwdWaitlistForm && pwdSizeGuide) {
    initSizeGuide(pwdSizeGuide, (mm) => {
      const radio = pwdWaitlistForm.querySelector('input[name="tray_size"][value="' + mm + '"]');
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        radio.closest('.pwd__group')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  const cartForm = document.querySelector('[data-cart-form]');
  if (cartForm) {
    const setQty = (key, qty) => {
      const input = cartForm.querySelector('.cart__qty-input[data-key="' + key + '"]');
      if (input) input.value = Math.max(0, qty);
    };

    cartForm.querySelectorAll('.cart__qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const input = cartForm.querySelector('.cart__qty-input[data-key="' + key + '"]');
        if (!input) return;
        const step = parseInt(btn.dataset.qtyStep, 10) || 0;
        input.value = Math.max(0, (parseInt(input.value, 10) || 0) + step);
        cartForm.querySelector('[name="update"]')?.click();
        // Fallback: submit the form with `update` action
        if (!cartForm.querySelector('[name="update"]')) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden'; hidden.name = 'update'; hidden.value = '';
          cartForm.appendChild(hidden);
          cartForm.submit();
        }
      });
    });

    cartForm.querySelectorAll('[data-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        setQty(key, 0);
        const hidden = cartForm.querySelector('[name="update"]') || (() => {
          const h = document.createElement('input');
          h.type = 'hidden'; h.name = 'update'; h.value = '';
          cartForm.appendChild(h);
          return h;
        })();
        cartForm.submit();
      });
    });
  }

  /* ==========================================================
     AI-render disclosure on rich-text images
     ----------------------------------------------------------
     Every photograph on the site is a render, and the ones inside
     article and page bodies arrive as raw HTML from Shopify, so
     the theme cannot wrap them in Liquid. Wrap them here instead
     and append the same mark the templates render.

     Size is read off the layout box, not naturalWidth: body images
     are loading="lazy", so naturalWidth stays 0 until the reader
     scrolls to them and gating on load left them unmarked.

     Skipped: anything already marked, SVGs, inline icons under
     200px, and images the author opted out of with
     data-ai-mark="false".
     ========================================================== */
  // Single source of truth is snippets/ai-mark-text.liquid, carried here on
  // <html data-ai-mark>. The literal is only a floor for layouts that do not
  // render it (the password page has no rich text of its own).
  const AI_MARK_TEXT = document.documentElement.dataset.aiMark
    || 'Rendered with AI. Illustration only.';

  const markRichTextImage = (img) => {
    if (img.dataset.aiMark === 'false') return;
    if (img.closest('.ai-mark-wrap')) return;
    if (/\.svg($|\?)/i.test(img.currentSrc || img.src || '')) return;

    const width = img.getBoundingClientRect().width
      || img.naturalWidth
      || parseInt(img.getAttribute('width'), 10)
      || 0;
    // 0 means not laid out yet — leave it for the window load pass.
    if (width === 0 || width < 200) return;

    const wrap = document.createElement('span');
    wrap.className = 'ai-mark-wrap';
    const mark = document.createElement('span');
    mark.className = 'ai-mark ai-mark--br ai-mark--sm';
    mark.textContent = AI_MARK_TEXT;

    img.parentNode.insertBefore(wrap, img);
    wrap.appendChild(img);
    wrap.appendChild(mark);
  };

  const markRichText = () => document.querySelectorAll('.rte img').forEach(markRichTextImage);
  markRichText();
  window.addEventListener('load', markRichText, { once: true });

})();
